import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import SearchComponent from '@components/inputs/search/Search.component';
import ConfirmModal from '@components/modal/Confirm.modal';
import NewTableComponent from '@components/table/NewTable.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import {Colors} from '@constants/colors.constants';
import {
  Add,
  Alert,
  BlackCheck,
  BlackMinus,
  BlackPlus,
  BlackTimer,
  CheckCircleGreen,
  CheckCircleGrey,
  CheckCircleOrange,
  Comment,
  Download,
  GreyCheck,
  GreyMinus,
  GreyPlus,
  GreyTimer,
  GroupAction,
} from '@icons/index';
import {Box, Button, IconButton, Stack, Typography} from '@mui/material';
import {
  GridCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
  GridValueSetterParams,
} from '@mui/x-data-grid-pro';
import {useEffectFn} from '@ngneat/effects-hooks';
import {emitOnce} from '@ngneat/elf';
import {useObservable} from '@ngneat/react-rxjs';
import ExecutionLineGroupedActionModal from '@screens/admin/invoices/executions/ExecutionLineGroupedAction.modal';
import {FilterBaseEntityEnum} from '@store/filters';
import {
  ExecutionLine,
  executionLineQuery,
  executionLineService,
  ExecutionLineState,
  ExecutionLineStatus,
  searchExecutionLinesEffect,
} from '@store/invoices/executionLines';
import {executionsService} from '@store/operations/executions';
import {NumberExt} from '@utils/extensions/number.extension';
import COLUMN_TYPES from '@utils/table/columnTypes.table';

import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {finalize, Observable} from 'rxjs';
import {InvoicingSource} from '@store/common/organisation.model';
import {ArrowBack, ArrowForward, BackspaceOutlined, DoneAll, WarningOutlined} from '@mui/icons-material';
import dayjs from 'dayjs';
import {GridStatePro} from '@mui/x-data-grid-pro/models/gridStatePro';
import SelectComponent from '@components/inputs/select/Select.component';
import {InvoiceStatus} from '@store/invoices/invoices';
import ExecutionReceiptModal from '@screens/admin/operations/executions/ExecutionReceipt.modal';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';
import Tooltip from '@mui/material/Tooltip';
import {ServiceValidationStatusEnum} from '@store/clients/services';

export const readyForInvoicingKey = 'READY_FOR_INVOICING_IDS';

const ExecutionsTab = (props: { invoicingSource: InvoicingSource }) => {
  const {invoicingSource} = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const searchExecutions = useEffectFn(searchExecutionLinesEffect);

  const [CSVLoading, setCSVLoading] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);

  const [openGroupedAction, setOpenGroupedAction] = useState(false);
  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);
  const [readyForInvoicing, setReadyForInvoicing] = useState<{ id: number; totalBuy: number; totalSell: number }[]>(
    executionLineQuery.getExecutionLinesReadyForInvoicing()
  );
  const [tableState, setTableState] = useState<GridStatePro | undefined>(undefined);

  const [{executionLines, loading}] = useObservable(executionLineQuery.executionLines$);
  const [selectedExecutionLines] = useObservable(executionLineQuery.selectedExecutionLines$);

  const [totalExecutions, setTotalExecutions] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);


  const [filters] = useObservable(executionLineQuery.filters$);
  const [sortModel] = useObservable(executionLineQuery.sort$);
  const [tablePagination] = useObservable(executionLineQuery.pagination$);

  useEffect(() => {
    executionLineService.setFilters('invoicingSource')(invoicingSource);
  }, [invoicingSource]);

  useEffect(() => {
    refresh();
  }, [filters, searchExecutions, sortModel, tablePagination]);

  const refresh = () => {
    setIsLoading(true);
    const customFilters = {
      from: filters.from,
      to: filters.to,
      search: filters.search,
      tableFilters: filters.tableFilters,
    };
    executionsService.countExecutionsWithParams(customFilters, sortModel, tablePagination).subscribe((data) => {
      setIsLoading(false);
      setTotalExecutions(data.count);
    });
    searchExecutions({filters, tablePagination, sort: sortModel});
  };

  useEffect(() => {
    localStorage.setItem(readyForInvoicingKey, JSON.stringify(readyForInvoicing));
    if (readyForInvoicing.length === 0) {
      executionLineService.setFilters('filterReadyForInvoicing')(false);
      executionLineService.setPagination('page')(1);
    }
  }, [readyForInvoicing]);

  const handleReceiptModal = () => {
    setOpenReceipt(false);
    setIsLoading(true);
    const customFilters = {
      from: filters.from,
      to: filters.to,
      search: filters.search,
      tableFilters: filters.tableFilters,
    };
    executionsService.countExecutionsWithParams(customFilters, sortModel, tablePagination).subscribe((data) => {
      setIsLoading(false);
      setTotalExecutions(data.count);
    });
    searchExecutions({filters, tablePagination, sort: sortModel});
  };

  const patchExecutionLine = (executionLineId: number, updatedExecutionLine: Partial<ExecutionLine>) => {
    executionLineService.patchExecutionLine(executionLineId, updatedExecutionLine).subscribe({
      next: (executionLine: ExecutionLine) => {
        const index = readyForInvoicing.findIndex(el => el.id === executionLine.id);
        if (index > -1) {
          const readyForInvoicingCopy = [...readyForInvoicing];
          readyForInvoicingCopy[index] = {
            id: executionLine.id,
            totalBuy: executionLine.totalBuy,
            totalSell: executionLine.totalSell
          };
          setReadyForInvoicing(readyForInvoicingCopy);
        }
      }, error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const updateReadyForInvoicingLocally = (executionLines: ExecutionLine[], isReadyForInvoicing: boolean) => {
    if (isReadyForInvoicing) {
      setReadyForInvoicing([
        ...readyForInvoicing,
        ...executionLines.map((executionLine) => ({
          id: executionLine.id,
          totalBuy: Math.round(executionLine.totalBuy),
          totalSell: Math.round(executionLine.totalSell),
        })),
      ]);
    } else {
      setReadyForInvoicing(
        readyForInvoicing.filter((el) => executionLines.map((executionLine) => executionLine.id).indexOf(el.id) === -1)
      );
    }
  };

  const updateExecutionLinesStatus = (data: {
    updateStatus?: ExecutionLineStatus;
    supplierInvoicingDelayed?: boolean;
    clientInvoicingDelayed?: boolean;
  }) => {
    executionLineService.updateExecutionLinesDelayed(data).subscribe({
      next: () => enqueueSnackbar(t('operations.executions.success.updateMany'), {variant: 'success'}),
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleConfirmValidation =
    (type: OtherActionType, executionLine: ExecutionLine) => (handleStopLoading: () => void) => {
      let validationObservable: Observable<any> | undefined = undefined;
      switch (type) {
        case OtherActionType.DELETE:
          updateReadyForInvoicingLocally([executionLine], false);
          validationObservable = executionsService.deleteExecution(executionLine.executionId);
          break;
      }
      validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
        next: () => {
          enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.execution`), {variant: 'success'});
          setOpenConfirmAction(false);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    };

  const handleExportCSV = () => {
    setCSVLoading(true);
    executionLineService.getExecutionLinesCSV()
      .pipe(finalize(() => setCSVLoading(false)))
      .subscribe({
        error: (err) => {
          enqueueSnackbar(err.text, err.options);
        },
      });
  };

  const handlePreviousDay = () => {
    if (filters.from && filters.to) {
      executionLineService.setFilters('from')(dayjs(filters.from).subtract(1, 'day').toDate());
      executionLineService.setFilters('to')(dayjs(filters.to).subtract(1, 'day').toDate());
    }
  };

  const handleNextDay = () => {
    if (filters.from && filters.to) {
      executionLineService.setFilters('from')(dayjs(filters.from).add(1, 'day').toDate());
      executionLineService.setFilters('to')(dayjs(filters.to).add(1, 'day').toDate());
    }
  };


  const totalSell = readyForInvoicing.reduce((acc, curr) => acc + curr.totalSell, 0);
  const totalBuy = readyForInvoicing.reduce((acc, curr) => acc + curr.totalBuy, 0);


  return (
    <Stack flex={1} spacing={2} pb="25px" mt="-35px">
      <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
        <ExpandableIconButtonComponent
          disabled={isLoading || !totalExecutions}
          variant="outlined"
          color="primary"
          onClick={() => setOpenReceipt(true)}
          startIcon={<DoneAll/>}
          title={t('invoices.executions.actions.seeNonExecutedOperations', {value: totalExecutions})}
        />
        <ExpandableIconButtonComponent
          variant="outlined" color="primary"
          onClick={() => setReadyForInvoicing([])}
          startIcon={<BackspaceOutlined/>}
          title={t('invoices.executions.actions.clearReadyForInvoicing')}
        />
        <ExpandableIconButtonComponent
          variant="outlined"
          color="primary"
          disabled={!selectedExecutionLines.length}
          onClick={() => setOpenGroupedAction(true)}
          startIcon={<GroupAction/>}
          title={t('global.groupedAction')}
        />
        <ExpandableIconButtonComponent
          disabled={CSVLoading}
          onClick={handleExportCSV}
          variant="outlined"
          color="primary"
          startIcon={<Download/>}
          title={t('global.exportCSV')}/>
        <ExpandableIconButtonComponent
          variant="contained"
          color="primary"
          onClick={() => navigate('add-batch', {replace: true})}
          startIcon={<Add/>}
          title={t('operations.addButtons.batchExecutions')}
        />
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={handlePreviousDay}><ArrowBack/></IconButton>
          <Box>
            <DateRangePickerComponent
              size="small"
              inputWidth={140}
              placeholderStart={t('invoices.executions.filters.from')}
              placeholderEnd={t('invoices.executions.filters.to')}
              startDate={filters.from}
              endDate={filters.to}
              handleChangeStartDate={executionLineService.setFilters('from')}
              handleChangeEndDate={executionLineService.setFilters('to')}/>
          </Box>
          <IconButton onClick={handleNextDay}><ArrowForward/></IconButton>
        </Stack>
        <Stack direction="column" alignItems="flex-end">
          <Box>
            <CheckboxComponent
              handleChange={(value) => {
                executionLineService.setFilters('filterReadyForInvoicing')(value);
                executionLineService.setPagination('page')(1);
              }}
              value={filters.filterReadyForInvoicing}
              label={t('invoices.executions.filters.filterReadyForInvoicing')}
            />
          </Box>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  executionLineService.setFilters('search')(value);
                  executionLineService.setPagination('page')(1);
                });
              }}
            />
          </Box><Box>
          <SelectComponent
            inputWidth={300}
            color="secondary"
            items={ExecutionLineState.selectItems()}
            value={filters.state}
            handleChange={executionLineService.setFilters('state')}
            placeholder={t('invoices.executions.filters.state')}
          />
        </Box>
          <Box>
            <SelectComponent
              inputWidth={250}
              color="secondary"
              items={[{label: t('global.all'), value: ''}, ...ServiceValidationStatusEnum.selectItems()]}
              value={filters.serviceValidationStatus ?? ''}
              handleChange={(v) => executionLineService.setFilters('serviceValidationStatus')(v || undefined)}
              placeholder={t('invoices.executions.filters.serviceValidationStatus')}
            />
          </Box></Stack>
        <TableFiltersComponent
          baseEntity={FilterBaseEntityEnum.EXECUTION_LINE}
          filters={filters}
          handleSave={executionLineService.setFilters('tableFilters')}
          handleSaveExclude={executionLineService.setFilters('excludeTableFilters')}
          handleReset={() => executionLineService.resetFilters()}/>
      </Stack>
      <Stack flex={1}>
        <NewTableComponent
          columnThreshold={0}
          loading={loading}
          editableFields={['executionLine.quantity', 'executionLine.puBuy', 'executionLine.puSell']}
          handleStateChange={setTableState}
          state={tableState}
          columns={[
            COLUMN_TYPES.TEXT({
              field: 'execution.executedAt',
              cellClassName: (params: GridCellParams<any, ExecutionLine>) =>
                params.row.executedAt ? 'success' : 'secondary',
              label: t('invoices.executions.table.executedAt'),
              sortable: true,
              editable: false,
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                params.row.executedAt ?? params.row.plannedFrom,
              valueFormatter: (params: GridValueFormatterParams) =>
                params.value ? t('global.date', {date: new Date(params.value)}) : '',
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'organisationUnit.name',
              color: 'secondary',
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                params.row.clientOrganisationUnit?.clientUnitName,
              label: t('invoices.executions.table.clientUnitName'),
              sortable: true,
              editable: false,
              minWidth: 200
            }),
            COLUMN_TYPES.TEXT({
              field: 'supOrgUnit.name',
              color: 'secondary',
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.supplierOrganisationUnit?.supplierUnitName,
              label: t('invoices.executions.table.supplierUnitName'),
              sortable: true,
              editable: false,
              width: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'name',
              color: 'secondary',
              label: t('invoices.executions.table.name'),
              fullWidth: true,
              editable: false,
              minWidth: 250,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'serviceKind.kind',
              label: t('invoices.executions.table.type'),
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row?.kind,
              sortable: true,
              editable: false,
              width: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'waste.name',
              color: 'secondary',
              label: t('invoices.executions.table.waste'),
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row?.waste,
              sortable: true,
              editable: false,
              width: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'container.name',
              color: 'secondary',
              label: t('invoices.executions.table.container'),
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row?.container,
              sortable: true,
              editable: false,
              width: 150,
            }),
            COLUMN_TYPES.NUMBER({
              field: 'executionLine.quantity',
              label: t('invoices.executions.table.quantity'),
              sortable: true,
              editable: true,
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row?.quantity,
              endAdornment: (row: ExecutionLine) => (<Typography fontSize={8}
                                                                 fontWeight="400">
                {row?.unit}
              </Typography>),
              valueSetter: (params: GridValueSetterParams) => ({
                ...params.row,
                quantity: params.value ?? 0,
                totalSell: (params.value ?? 0) * (params.row.puSell ?? 0) * 100,
                totalBuy: (params.value ?? 0) * (params.row.puBuy ?? 0) * 100,
              }),
              width: 130,
            }),
            COLUMN_TYPES.NUMBER({
              field: 'executionLine.unitCostInCents',
              sortable: true,
              editable: true,
              label: t('invoices.executions.table.puBuy'),
              disabled: (executionLine: ExecutionLine) => executionLine.supplierInvoiceLineId,
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                params.row.puBuy ? NumberExt.round(params.row.puBuy / 100, 2) : undefined,
              valueSetter: (params: GridValueSetterParams) => ({
                ...params.row,
                puBuy: Math.round((params.value ?? 0) * 100),
                totalBuy: (params.value ?? 0) * (params.row.quantity ?? 0) * 100,
              }),
              width: 150,
              endAdornment: (row: ExecutionLine) => (<Typography fontSize={8}
                                                                 fontWeight="400">
                {row?.supplierOrganisationUnit?.currency}
              </Typography>),
            }),
            COLUMN_TYPES.NUMBER({
              field: 'executionLine.unitPriceInCents',
              disabled: (executionLine: ExecutionLine) => executionLine.clientInvoiceLineId,
              sortable: true,
              editable: true,
              inputColor: (executionLine: ExecutionLine) =>
                executionLine.puSell <= executionLine.puBuy ? Colors.error : undefined,
              label: t('invoices.executions.table.puSell'),
              valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                params.row.puSell ? NumberExt.round(params.row.puSell / 100, 2) : undefined,
              valueSetter: (params: GridValueSetterParams) => {
                return {
                  ...params.row,
                  puSell: Math.round((params.value ?? 0) * 100),
                  totalSell: (params.value ?? 0) * (params.row.quantity ?? 0) * 100
                };
              },
              endAdornment: (row: ExecutionLine) => (<Typography fontSize={8}
                                                                 fontWeight="400">
                {row?.clientOrganisationUnit?.currency}
              </Typography>),
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'totalBuy',
              sortable: true,
              editable: false,
              label: t('invoices.executions.table.totalBuy'),
              valueFormatter: (params: GridValueFormatterParams) =>
                params.value ? NumberExt.round(params.value / 100, 2) : '',
              width: 100,
            }),
            COLUMN_TYPES.TEXT({
              field: 'totalSell',
              sortable: true,
              editable: false,
              label: t('invoices.executions.table.totalSell'),
              valueFormatter: (params: GridValueFormatterParams) =>
                params.value ? NumberExt.round(params.value / 100, 2) : '',
              width: 100,
            }),
            COLUMN_TYPES.ICONS({
              field: 'properties',
              label: '',
              editable: false,
              icons: [
                {
                  icon: <Tooltip title="En attente de prix de vente">
                    <WarningOutlined color="warning" fontSize="small"/>
                  </Tooltip>,
                  hidden: (el: ExecutionLine) => el.serviceValidationStatus !== ServiceValidationStatusEnum.WAITING_FOR_SP
                },
                {
                  icon: <Tooltip title="Contient des commentaires"><Comment
                    style={{width: 18, marginTop: 5, height: 21}}/></Tooltip>,
                  hidden: (execution: ExecutionLine) => (execution.commentsCount ?? 0) <= 0,
                },
                {
                  icon: <Tooltip title="Contient des non-conformités"><Alert
                    style={{height: 18, width: 21}}/></Tooltip>,
                  hidden: (execution: ExecutionLine) => (execution.issuesCount ?? 0) <= 0,
                },
              ],
              width: 90,
            }),
            ...(invoicingSource === InvoicingSource.MOS ? [COLUMN_TYPES.CHECKS({
              field: 'executionLineStatus',
              editable: false,
              checkAll: {
                checkedIcon: <BlackCheck/>,
                uncheckedIcon: <BlackCheck/>,
                handleClick: (_, checked: boolean) => {
                  updateReadyForInvoicingLocally(
                    checked
                      ? executionLines.filter(
                        (el) =>
                          !readyForInvoicing.some((rfi) => rfi.id === el.id) &&
                          el.supplierInvoicingRequired &&
                          !el.supplierInvoiceLineId
                      )
                      : executionLines,
                    checked
                  );
                },
              },
              uncheckAll: {
                checkedIcon: <GreyCheck/>,
                uncheckedIcon: <GreyCheck/>,
                handleClick: (_, checked: boolean) => {
                  updateReadyForInvoicingLocally(
                    checked
                      ? executionLines.filter(
                        (el) =>
                          !readyForInvoicing.some((rfi) => rfi.id === el.id) &&
                          el.supplierInvoicingRequired &&
                          !el.supplierInvoiceLineId
                      )
                      : executionLines,
                    checked
                  );
                },
              },
              checks: [
                {
                  isChecked: (executionLine: ExecutionLine) => executionLine.readyForInvoicing,
                  checkedIcon: <BlackCheck/>,
                  uncheckedIcon: <GreyCheck/>,
                  hidden: (executionLine: ExecutionLine) => !executionLine.supplierInvoicingRequired || !!executionLine.supplierInvoiceLineId,
                  handleClick: (executionLine: ExecutionLine, checked: boolean) => {
                    updateReadyForInvoicingLocally([executionLine], checked);
                  },
                },
              ],
              width: 100,
            }),
              COLUMN_TYPES.CHECKS({
                field: 'invoicingRequired',
                cellClassName: () => 'with-space',
                editable: false,
                label: t(
                  `${t('invoices.executions.table.purchaseStatementShort')} / ${t(
                    'invoices.executions.table.billsShort'
                  )}`
                ),
                checks: [
                  {
                    isChecked: (executionLine: ExecutionLine) => executionLine.supplierInvoicingRequired,
                    checkedIcon: <BlackMinus/>,
                    uncheckedIcon: <GreyMinus/>,
                    hidden: (executionLine: ExecutionLine) => !!executionLine.supplierInvoiceLineId,
                    handleClick: (executionLine: ExecutionLine, checked: boolean) => {
                      patchExecutionLine(executionLine.id, {
                        supplierInvoicingRequired: checked,
                      });
                    },
                  },
                  {
                    isChecked: (executionLine: ExecutionLine) => executionLine.clientInvoicingRequired,
                    checkedIcon: <BlackPlus/>,
                    uncheckedIcon: <GreyPlus/>,
                    hidden: (executionLine: ExecutionLine) => !!executionLine.clientInvoiceLineId,
                    handleClick: (executionLine: ExecutionLine, checked: boolean) => {
                      patchExecutionLine(executionLine.id, {
                        clientInvoicingRequired: checked,
                      });
                    },
                  },
                ],
                width: 80,
              }),
              COLUMN_TYPES.ICON({
                field: 'supplierInvoiceLineId',
                editable: false,
                label: t('invoices.executions.table.purchaseStatementShort'),
                renderIcon: (executionLine: ExecutionLine) => {
                  if (!executionLine.supplierInvoicingRequired) return <Box>-</Box>;
                  if (!executionLine.supplierInvoiceLineId) return <CheckCircleGrey/>;
                  return <IconButton
                    onClick={() => navigate(`/invoices/purchase-statement/${executionLine.supplierInvoiceId}`)}> {executionLine.supplierInvoiceLineId ?
                    <CheckCircleGreen/> : <CheckCircleGrey/>}</IconButton>;
                },
                width: 40,
              }),
              COLUMN_TYPES.ICON({
                field: 'clientInvoiceLineId',
                editable: false,
                label: t('invoices.executions.table.billsShort'),
                renderIcon: (executionLine: ExecutionLine) => {
                  if (!executionLine.clientInvoicingRequired) return <Box>-</Box>;
                  if (!executionLine.clientInvoiceLineId) return <CheckCircleGrey/>;
                  return <IconButton
                    onClick={() => navigate(`/invoices/bills/${executionLine.clientInvoiceId}`)}> {executionLine.invoiceStatus === InvoiceStatus.DRAFT ? (
                    <CheckCircleOrange/>
                  ) : (
                    <CheckCircleGreen/>
                  )}
                  </IconButton>;
                },
                width: 40,
              }),
              COLUMN_TYPES.CHECKS({
                field: 'supplierInvoicingDelayed',
                editable: false,
                checkAll: {
                  checkedIcon: (
                    <Stack alignItems="center">
                      <BlackTimer style={{height: '10px', width: '10px'}}/>
                      <Typography fontSize={8} fontWeight="500" color="#172134">
                        {t('invoices.executions.table.purchaseStatementShort')}
                      </Typography>
                    </Stack>
                  ),
                  uncheckedIcon: (
                    <Stack alignItems="center">
                      <GreyTimer style={{height: '10px', width: '10px'}}/>
                      <Typography fontSize={8} fontWeight="500" color="#848F9D">
                        {t('invoices.executions.table.purchaseStatementShort')}
                      </Typography>
                    </Stack>
                  ),
                  handleClick: (_, checked: boolean) => updateExecutionLinesStatus({supplierInvoicingDelayed: checked}),
                },
                checks: [
                  {
                    isChecked: (executionLine: ExecutionLine) => executionLine.supplierInvoicingDelayed,
                    checkedIcon: <BlackTimer/>,
                    uncheckedIcon: <GreyTimer/>,
                    hidden: (executionLine: ExecutionLine) =>
                      !executionLine.supplierInvoicingRequired || !!executionLine.supplierInvoiceLineId,
                    handleClick: (executionLine: ExecutionLine, checked: boolean) =>
                      patchExecutionLine(executionLine.id, {supplierInvoicingDelayed: checked}),
                  },
                ],
                width: 40,
              }),
              COLUMN_TYPES.CHECKS({
                field: 'clientInvoicingDelayed',
                editable: false,
                checkAll: {
                  checkedIcon: (
                    <Stack alignItems="center">
                      <BlackTimer style={{height: '10px', width: '10px'}}/>
                      <Typography fontSize={8} fontWeight="500" color="#172134">
                        {t('invoices.executions.table.billsShort')}
                      </Typography>
                    </Stack>
                  ),
                  uncheckedIcon: (
                    <Stack alignItems="center">
                      <GreyTimer style={{height: '10px', width: '10px'}}/>
                      <Typography fontSize={8} fontWeight="500" color="#848F9D">
                        {t('invoices.executions.table.billsShort')}
                      </Typography>
                    </Stack>
                  ),
                  handleClick: (_, checked: boolean) => updateExecutionLinesStatus({clientInvoicingDelayed: checked}),
                },
                checks: [
                  {
                    isChecked: (executionLine: ExecutionLine) => executionLine.clientInvoicingDelayed,
                    checkedIcon: <BlackTimer/>,
                    uncheckedIcon: <GreyTimer/>,
                    hidden: (executionLine: ExecutionLine) =>
                      !executionLine.clientInvoicingRequired || !!executionLine.clientInvoiceLineId,
                    handleClick: (executionLine: ExecutionLine, checked: boolean) =>
                      patchExecutionLine(executionLine.id, {clientInvoicingDelayed: checked}),
                  },
                ],
                width: 40,
              })] : []),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (executionLine: ExecutionLine) =>
                executionLine.executionId ? navigate(`${executionLine.executionId}`) : undefined,
              otherActions: [
                {type: OtherActionType.EDIT},
                {
                  type: OtherActionType.DELETE,
                  handleClick: (executionLine: ExecutionLine) =>
                    setOpenConfirmAction({
                      type: OtherActionType.DELETE,
                      row: executionLine,
                    }),
                },
              ],
            }),
          ]}
          handleReload={() => refresh()}
          handlePageSizeChange={executionLineService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => executionLineService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={executionLineService.setSort}
          sortModel={[sortModel]}
          disableRowSelectionOnClick={true}
          handleRowSelected={executionLineService.setSelectedExecutionLines}
          rowsSelected={selectedExecutionLines}
          totalRows={executionLineQuery.totalRows}
          autoHeight={false}
          handleRowChanged={(executionLine: ExecutionLine) => {
            const updatedExecutionLine = {
              puBuy: executionLine.puBuy,
              puSell: executionLine.puSell,
              quantity: executionLine.quantity,
            };
            patchExecutionLine(executionLine.id, updatedExecutionLine);
            return {...executionLine, ...updatedExecutionLine};
          }}
          isCellEditable={(params: GridCellParams<ExecutionLine>) => {
            if (params.field === 'executionLine.puSell' && params.row.clientInvoiceLineId) {
              return false;
            }
            if (params.field === 'executionLine.puBuy' && params.row.supplierInvoiceLineId) {
              return false;
            }

            if (params.field === 'executionLine.quantity' && (params.row.clientInvoiceLineId || params.row.supplierInvoiceLineId)) {
              return false;
            }

            return true;
          }}
          data={executionLines.map((el) => {
            el.readyForInvoicing = readyForInvoicing.some(r => r.id === el.id);
            return el;
          })}/>
        <Stack direction="row" alignItems="center" alignSelf="flex-end" pt="25px" spacing={2}>
          <Typography fontWeight="400" fontSize="14px" color={Colors.textWithOpacity}>
            {t('operations.executions.totalAmount')}
          </Typography>
          <Box p="7px" bgcolor={Colors.white}>
            <Typography fontWeight="400" fontSize="14px" color={Colors.textWithOpacity}>
              {NumberExt.round(totalBuy / 100, 2)} CHF
            </Typography>
          </Box>
          <Box p="7px" bgcolor={Colors.white}>
            <Typography fontWeight="400" color={Colors.textWithOpacity} fontSize="14px">
              {NumberExt.round(totalSell / 100, 2)} CHF
            </Typography>
          </Box>
          <Button
            onClick={() => {
              if (readyForInvoicing.length < 1) {
                enqueueSnackbar(t('invoices.executions.table.noExecutionLineReadyForInvoicing'), {variant: 'error'});
              } else {
                navigate('generate');
              }
            }}
            variant="contained"
            color="primary"
            size="large"
          >
            {t('global.generate')}
          </Button>
        </Stack>
      </Stack>
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.execution`)}
          handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)}/>
      )}
      {openGroupedAction && <ExecutionLineGroupedActionModal handleClose={() => setOpenGroupedAction(false)}/>}
      {openReceipt &&
          <ExecutionReceiptModal handleClose={() => setOpenReceipt(false)} onSave={() => handleReceiptModal()}
                                 filters={filters} sortModel={sortModel} tablePagination={tablePagination}/>}
      <Outlet/>
    </Stack>
  );
};

export default ExecutionsTab;
